import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import { useStoreAccount } from "../store/account"
import { EnsureLicenseCard, ListLicenseCards, RevokeLicenseCard } from "../api"
import ActivateLicenseDialog from "./ActivateLicenseDialog"
import { Section, Card, Loading, ErrorText } from "./ui"

/** Only premium subscriptions unlock a card (mirrors LicenseCard.isSubscribed on the API). */
const canActivateWith = (account) => typeof account?.account_type === "string" && account.account_type.includes("premium-")

/** The Aldoo Kids license card (old licenseCard.jsx): logo, KIDS, the printed code. */
function LicenseCardView({ license, onClick }) {
  const t = useT()
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="license-card"
      data-lcid={license.lcid}
      data-paired={license.paired ? "true" : "false"}
      className="relative flex w-full max-w-[440px] aspect-[440/233] flex-col justify-between rounded-[10px] bg-cover bg-center p-3 sm:p-4 text-left shadow-[5px_5px_15px_rgba(0,0,0,0.3)] transition-transform hover:scale-[1.03]"
      style={{ backgroundImage: "url(/static/images/license_card-image.webp)" }}
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start">
          <img src="/static/p-images/aldoo--277x58.webp" alt="Aldoo" className="w-[100px] sm:w-[132px] h-auto" />
          <span className="ml-1.5 -mt-1 text-[16px] sm:text-[20px] font-bold text-primary">KIDS</span>
        </div>
        <div className="flex flex-col items-end text-white">
          <span className="text-[16px] sm:text-[20px] font-bold leading-none">{t`License:`}</span>
          <span className="text-[16px] sm:text-[20px] leading-tight" data-testid="license-code">
            {license.code}
          </span>
        </div>
      </div>
      <span className="text-[10px] sm:text-[12px] font-bold uppercase text-black">{t`A Learning Platform For Children 3-7 y.o.`}</span>
    </button>
  )
}

// `ensure` is not idempotent under concurrent calls on older API builds and
// React (StrictMode) may run the effect twice: share one in-flight call.
let ensuring = null
async function loadLicenses() {
  const cards = await ListLicenseCards()
  if (Array.isArray(cards) && cards.length) return cards
  ensuring = ensuring || EnsureLicenseCard().finally(() => (ensuring = null))
  await ensuring
  return ListLicenseCards()
}

/**
 * Add-ons: the Aldoo Kids license cards of the account (old licenses.jsx).
 * `license-card {ensure}` gives the user a card, `{list}` shows it; clicking
 * the card opens the activation dialog; a paired card can be revoked so it
 * can be activated on another device.
 */
export default function AddOns() {
  const t = useT()
  const { account } = useStoreAccount()
  const [mobile, setMobile] = useState(false)
  const [active, setActive] = useState(null)
  const [revoking, setRevoking] = useState(null)
  const [actionError, setActionError] = useState("")
  const { result, pending, error, refresh } = useAsync(loadLicenses, [])
  const licenses = Array.isArray(result) ? result : []

  useEffect(() => {
    setMobile(/Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
  }, [])

  const revoke = async (license) => {
    if (!window.confirm(t`Revoke this license? The paired device will lose access until the card is activated again.`)) return
    setRevoking(license.lcid)
    setActionError("")
    const res = await RevokeLicenseCard(license.lcid).catch((e) => ({ error: e?.message || "request_failed" }))
    setRevoking(null)
    if (res?.error) return setActionError(res.error)
    refresh()
  }

  return (
    <Section title={t`Add-ons`}>
      {pending && !result && <Loading />}
      <ErrorText>{error?.message || result?.error}</ErrorText>
      <ErrorText>{actionError}</ErrorText>
      {licenses.map((license) => (
        <Card key={license.lcid} className="flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-12" data-testid="add-on">
          <div className="flex w-full max-w-[440px] flex-col items-center gap-3">
            <LicenseCardView license={license} onClick={() => setActive(license)} />
            {license.paired ? (
              <div className="flex items-center gap-4">
                <span className="text-[16px] font-bold text-primary" data-testid="license-active">
                  {t`License Active`}
                </span>
                <button type="button" onClick={() => revoke(license)} disabled={revoking === license.lcid} className="text-[14px] text-text_secondary hover:underline disabled:opacity-60" data-testid="license-revoke">
                  {t`Revoke`}
                </button>
              </div>
            ) : (
              <span className="text-[16px] text-text_secondary">{mobile ? t`Tap to activate` : t`Click to activate`}</span>
            )}
          </div>
          <div className="flex flex-col gap-4 max-w-[550px] text-center lg:text-left">
            <h3 className="m-0 text-[22px] md:text-[26px] font-bold">{t`License_Title`}</h3>
            <p className="m-0 text-[16px]">{t`license_line_1`}</p>
            <p className="m-0 text-[16px]">{t`license_line_2`}</p>
            <div>
              <Button variant="secondary" onClick={() => setActive(license)} data-testid="license-activate">
                {license.paired ? t`lc_your_license_is_now_active` : t`lc_activate_license`}
              </Button>
            </div>
          </div>
        </Card>
      ))}
      <ActivateLicenseDialog
        open={!!active}
        license={active}
        canActivate={canActivateWith(account)}
        mobile={mobile}
        onClose={() => setActive(null)}
        onPaired={() => refresh()}
      />
    </Section>
  )
}
